import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { campaignsApi, type MarketingCampaign } from '../api/campaignsApi';
import { STATUS_LABEL, DEPLOY_STATUS_COLOR, DEPLOY_STATUS_LABEL, channelLabel } from '../constants';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import { Button } from '@/shared/ui/Button';
import { PageHeader } from '@/shared/ui';
import { useLocation } from '@/shared/hooks/useLocation';
import { useAuth } from '@/shared/hooks/useAuth';
import { useWorkflowStatus } from '@/shared/hooks/useWorkflows';
import { campaignKeys } from '../queryKeys';
import styles from './AdsDashboardPage.module.css';

// ─── Page ────────────────────────────────────────────────────────────────────

export function AdsRecommendationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentLocation } = useLocation();
  const { user } = useAuth();
  const locationId = currentLocation?.id ?? '';

  const {
    isRunning: campaignRecsRunning,
    isFailed: campaignRecsFailed,
    error: campaignRecsError,
  } = useWorkflowStatus(locationId, 'campaign_recommendation', [[...campaignKeys.marketing()]]);

  const campaignsQuery = useQuery({
    queryKey: campaignKeys.marketingByLocation(locationId),
    queryFn: () => campaignsApi.list(locationId),
  });

  const recommendMutation = useMutation({
    mutationFn: () => campaignsApi.recommend(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: campaignKeys.marketing() }),
  });

  const dismissMutation = useMutation({
    mutationFn: (campaignId: string) => campaignsApi.dismiss(campaignId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: campaignKeys.marketing() }),
  });

  const allCampaigns: MarketingCampaign[] = campaignsQuery.data?.data ?? [];
  // Show all non-dismissed campaigns here (recommended + in-progress ones)
  const recommendations = allCampaigns.filter((c) => c.status !== 'dismissed');

  const latestGeneratedAt = recommendations.length
    ? recommendations.reduce((latest, c) => {
        const t = c.generatedAt ?? c.createdAt;
        return t > latest ? t : latest;
      }, recommendations[0].generatedAt ?? recommendations[0].createdAt)
    : null;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Campaign Recommendations"
        subtitle="AI-suggested campaigns based on your practice data"
        timestamp={latestGeneratedAt}
        actions={
          <Button
            onClick={() => recommendMutation.mutate()}
            isLoading={recommendMutation.isPending}
          >
            Create Recommendations
          </Button>
        }
      />

      {campaignRecsRunning ? (
        <div className={styles.workflowCard}>
          <div className={styles.workflowSpinner} />
          <div className={styles.workflowTitle}>Generating Campaign Recommendations</div>
          <div className={styles.workflowSubtext}>
            Analyzing your strategy and practice data to create campaign briefs. Check back in 5–10
            minutes.
          </div>
        </div>
      ) : campaignRecsFailed ? (
        <div className={styles.workflowCardFailed}>
          <div className={styles.workflowTitle}>Campaign Recommendation Generation Failed</div>
          <div className={styles.workflowSubtext}>
            Something went wrong generating campaign recommendations. Please try again.
          </div>
          {user?.isAgency && campaignRecsError && (
            <div className={styles.workflowErrorDetails}>{campaignRecsError}</div>
          )}
        </div>
      ) : campaignsQuery.isLoading ? (
        <SkeletonCard />
      ) : recommendations.length === 0 ? (
        <div className={styles.emptyRecs}>
          <p>No recommendations yet.</p>
          <p>
            Click "Create Recommendations" and our AI will analyze your practice data to suggest the
            right campaigns to run.
          </p>
        </div>
      ) : (
        <div className={styles.recCards}>
          {recommendations.map((mc) => (
            <RecommendationCard
              key={mc.id}
              campaign={mc}
              onReview={() => navigate(`/campaigns/${mc.id}`)}
              onDismiss={() => dismissMutation.mutate(mc.id)}
              isDismissing={dismissMutation.isPending && dismissMutation.variables === mc.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Recommendation card ─────────────────────────────────────────────────────

function RecommendationCard({
  campaign,
  onReview,
  onDismiss,
  isDismissing,
}: {
  campaign: MarketingCampaign;
  onReview: () => void;
  onDismiss: () => void;
  isDismissing: boolean;
}) {
  const brief = campaign.brief as MarketingCampaign['brief'] & {
    _rationale?: string;
    platforms?: string[];
  };
  const rationale = brief?._rationale;
  const platforms = brief?.platforms ?? [];
  const deployments = campaign.campaigns ?? [];

  const statusBadgeClass = styles[`badge_${campaign.status}`] ?? styles.badge_draft;

  return (
    <div className={styles.recCard}>
      <div className={styles.recCardBody}>
        <div className={styles.recCardHeader}>
          <span className={styles.recCardName}>{campaign.name}</span>
          <span className={`${styles.recCardBadge} ${statusBadgeClass}`}>
            {STATUS_LABEL[campaign.status] ?? campaign.status}
          </span>
          {campaign.objective && (
            <span className={`${styles.recCardBadge} ${styles.badge_draft}`}>
              {campaign.objective}
            </span>
          )}
        </div>

        {rationale && <p className={styles.recCardRationale}>{rationale}</p>}

        <div className={styles.recCardMeta}>
          {brief?.offer && <span>Offer: {brief.offer}</span>}
          {platforms.length > 0 && <span>Platforms: {platforms.map(channelLabel).join(', ')}</span>}
          {brief?.monthlyBudget && (
            <span>${Math.round(brief.monthlyBudget).toLocaleString()}/mo</span>
          )}
        </div>

        {/* Per-deployment status pills */}
        {deployments.length > 0 && (
          <div className={styles.deployStatusList}>
            {deployments.map((d) => (
              <span key={d.id} className={styles.deployPill}>
                <span
                  className={styles.deployPillDot}
                  style={{ background: DEPLOY_STATUS_COLOR[d.deployStatus] ?? 'var(--gray-500)' }}
                />
                {channelLabel(d.channel ?? d.platform)} —{' '}
                {DEPLOY_STATUS_LABEL[d.deployStatus] ?? d.deployStatus}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className={styles.recCardActions}>
        <Button size="sm" onClick={onReview}>
          Review
        </Button>
        {campaign.status === 'recommended' && (
          <Button variant="tertiary" size="sm" onClick={onDismiss} isLoading={isDismissing}>
            Dismiss
          </Button>
        )}
      </div>
    </div>
  );
}
